/**
 * Installation Tech Dialog - For installation technicians to connect customers
 */
import React, { useState, useEffect } from 'react';
import { Pole } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { usePoleStore, useCableStore } from '../../stores';
import { indexPortovSplitterov, portNaVlakne } from '../../utils/zakaznickeVlakna';
import './InstallationTechDialog.css';

interface InstallationTechDialogProps {
  pole: Pole;
  projectId: number;
  onClose: () => void;
  onCustomerConnected?: () => void;
  /**
   * Prípojka na inom stĺpiku. Technik stojí pri kazete, kde sa vlákno zvára
   * k domu, ale splitter je inde — dialóg sa otvorí rovno nad tým portom
   * a zapisuje sa presne do kazety, ktorú tu dostane, bez dohadovania.
   */
  preselect?: {
    poleId: number;
    splitterIndex: number;
    outputNumber: number;
    /** čo technik v checkliste videl — ukáže sa v hlavičke */
    popis?: string;
  };
}

interface SplitterOutput {
  splitter_id: number;
  splitter_type: string;
  output_number: number;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  installation_notes?: string;
  installed_by?: string;
  installed_at?: string;
  pole_id?: number; // ID of pole where this customer is connected (parent or virtual)
  cassette_type?: 'normal' | 'customer'; // Type of cassette where customer is connected
}

interface SplitterInfo {
  id: number;
  type: string;
  input_cable?: string;
  input_fiber?: number;
  input_buffer?: number;
  outputs: SplitterOutput[];
}

const API_URL = process.env.REACT_APP_API_URL || '';

export const InstallationTechDialog: React.FC<InstallationTechDialogProps> = ({
  pole,
  projectId,
  onClose,
  onCustomerConnected,
  preselect,
}) => {
  const { token, user, isAdmin } = useAuthStore();
  const { jePripajanie } = useRezimPrace();
  const { poles, fetchPoles } = usePoleStore();
  const { cables } = useCableStore();
  const [splitters, setSplitters] = useState<SplitterInfo[]>([]);
  const [selectedOutput, setSelectedOutput] = useState<SplitterOutput | null>(null);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [showMoveForm, setShowMoveForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [hasVirtualPole, setHasVirtualPole] = useState(false);
  const [actualPoleId, setActualPoleId] = useState<number>(pole.id); // ID of pole with customer cassette
  const [connectMode, setConnectMode] = useState<'existing' | 'manual'>('existing');
  const [pripajamZPripojky, setPripajamZPripojky] = useState<any | null>(null);
  const [pendingCustomers, setPendingCustomers] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    notes: '',
  });

  // Fiber color mapping - same as in InstallationChecklistDialog
  const colors = ['modrá', 'oranžová', 'zelená', 'hnedá', 'šedá', 'biela', 'červená', 'čierna', 'žltá', 'fialová', 'ružová', 'tyrkysová'];

  const fiberColorStyles: { [key: string]: { bg: string; text: string } } = {
    'modrá': { bg: '#E3F2FD', text: '#0D47A1' },
    'oranžová': { bg: '#FFF3E0', text: '#E65100' },
    'zelená': { bg: '#E8F5E9', text: '#1B5E20' },
    'hnedá': { bg: '#EFEBE9', text: '#4E342E' },
    'šedá': { bg: '#F5F5F5', text: '#424242' },
    'biela': { bg: '#FAFAFA', text: '#212121' },
    'červená': { bg: '#FFEBEE', text: '#B71C1C' },
    'čierna': { bg: '#EEEEEE', text: '#000000' },
    'žltá': { bg: '#FFFDE7', text: '#F57F17' },
    'fialová': { bg: '#F3E5F5', text: '#4A148C' },
    'ružová': { bg: '#FCE4EC', text: '#880E4F' },
    'tyrkysová': { bg: '#E0F2F1', text: '#004D40' },
  };

  const getFiberColorName = (fiberNumber: number): string => {
    return colors[(fiberNumber - 1) % colors.length];
  };

  const getFiberColorStyle = (fiberNumber: number): { bg: string; text: string } => {
    const colorName = getFiberColorName(fiberNumber);
    return fiberColorStyles[colorName] || { bg: '#FFFFFF', text: '#000000' };
  };

  const getBufferColorName = (bufferNumber: number): string => {
    return colors[(bufferNumber - 1) % colors.length];
  };

  const getBufferColorStyle = (bufferNumber: number): { bg: string; text: string } => {
    const colorName = getBufferColorName(bufferNumber);
    return fiberColorStyles[colorName] || { bg: '#FFFFFF', text: '#000000' };
  };

  // Prípojky k zákazníkom na tomto stĺpiku. Vlákno sem prišlo zo zákazníckeho
  // splittra, ktorý môže stáť na úplne inom stĺpiku — technik tu má vidieť, či
  // je ten port voľný, a dať sa naň pripojiť zákazník bez chodenia inam.
  const pripojky = React.useMemo(() => {
    if (preselect) return [];   // v nore-otvorenom dialógu už prípojky neriešime
    const freshPole = poles.find(p => p.id === pole.id) || pole;
    const dvojca = poles.find(p => p.parent_pole_id === freshPole.id && p.is_virtual);
    const zdroje: { drops: any[]; kde: string }[] = [
      { drops: ((freshPole.cassette as any)?.drops || []), kde: '' },
      { drops: ((dvojca?.cassette as any)?.drops || []), kde: ' (zákaznícka kazeta)' },
    ];
    const index = indexPortovSplitterov(poles);
    const out: any[] = [];
    zdroje.forEach(({ drops, kde }) => {
      drops.forEach((d: any, i: number) => {
        const kabel = cables.find(c => c.id === d.cable_id);
        const odkial = kabel
          ? `${poles.find(p => p.id === kabel.from_pole_id)?.name || '?'} → ${poles.find(p => p.id === kabel.to_pole_id)?.name || '?'}`
          : `kábel #${d.cable_id}`;
        out.push({
          kluc: `${kde}_${i}`,
          fiber: d.fiber,
          label: d.label,
          kde,
          odkial,
          port: portNaVlakne(index, d.cable_id, d.fiber),
        });
      });
    });
    return out;
  }, [poles, cables, pole, preselect]);

  useEffect(() => {
    loadSplitters();
  }, [pole.id, poles]);

  // Otvor formulár rovno nad portom, z ktorého technik prišiel. Len raz —
  // po pripojení sa zoznam znovu načíta a formulár sa už otvárať nemá.
  const predvyberSpraveny = React.useRef(false);
  useEffect(() => {
    if (!preselect || predvyberSpraveny.current || splitters.length === 0) return;
    const sp = splitters.find(x => x.id === preselect.splitterIndex);
    const out = sp?.outputs.find(o => o.output_number === preselect.outputNumber);
    if (!out) return;
    predvyberSpraveny.current = true;
    setSelectedOutput(out);
    if (!out.customer_name) setShowConnectForm(true);
  }, [splitters, preselect]);

  useEffect(() => {
    loadPendingCustomers();
  }, []);

  const loadPendingCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/installation/pending-customers`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingCustomers(data);
      }
    } catch (error) {
      console.error('Failed to load pending customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadSplitters = () => {
    // Get fresh pole data from store (in case it was updated)
    const freshPole = poles.find(p => p.id === pole.id) || pole;

    console.log('🔍 [InstallationTechDialog] loadSplitters:', {
      pole_id: pole.id,
      pole_name: pole.name,
      freshPole_id: freshPole.id,
      freshPole_name: freshPole.name,
      freshPole_cassette: freshPole.cassette,
      total_poles: poles.length
    });

    // Prípojka: technik prišiel z checklistu konkrétneho portu na konkrétnom
    // stĺpiku. Nič sa neprepína na virtuálne dvojča a nič sa nefiltruje podľa
    // role — ukáž presne tú kazetu, v ktorej ten splitter stojí.
    if (preselect) {
      const cielovy = poles.find(p => p.id === preselect.poleId) || freshPole;
      const zoznam: SplitterInfo[] = ((cielovy.cassette?.splitters || []) as any[]).map(
        (splitter: any, idx: number) => ({
          id: idx,
          type: splitter.type || '1:8',
          input_cable: splitter.input_cable_name,
          input_fiber: splitter.input_fiber,
          input_buffer: splitter.input_buffer,
          outputs: ((splitter.outputs || []) as any[]).map((output: any, outIdx: number) => ({
            splitter_id: idx,
            splitter_type: splitter.type || '1:8',
            output_number: outIdx + 1,
            customer_name: output.customer_name,
            customer_phone: output.customer_phone,
            customer_address: output.customer_address,
            installation_notes: output.installation_notes,
            installed_by: output.installed_by,
            installed_at: output.installed_at,
            pole_id: cielovy.id,
            cassette_type: cielovy.is_virtual ? 'customer' : 'normal',
          })),
        }),
      );
      setHasVirtualPole(!!cielovy.is_virtual);
      setActualPoleId(cielovy.id);
      setSplitters(zoznam);
      return;
    }

    // Find virtual pole (customer cassette) - same logic as in MapView
    const virtualPole = poles.find(p => p.is_virtual && p.parent_pole_id === freshPole.id);

    console.log('🔍 [InstallationTechDialog] virtualPole:', {
      found: !!virtualPole,
      virtualPole_id: virtualPole?.id,
      virtualPole_name: virtualPole?.name,
      virtualPole_cassette: virtualPole?.cassette
    });

    // Track if we have a virtual pole
    setHasVirtualPole(!!virtualPole);

    // Load splitters based on user role
    const loadedSplitters: SplitterInfo[] = [];
    // Aj admin s prepnutým pohľadom má vidieť presne to, čo montážnik —
    // inak prepínač ukáže iný zoznam splittrov než ten, o ktorom hovoríme.
    const isInstallTech = user?.role === 'install_tech' || jePripajanie;

    console.log('🔍 [InstallationTechDialog] User role:', user?.role, 'isInstallTech:', isInstallTech);

    // For install_tech users:
    //   - If virtual pole exists: load ONLY from virtual pole (customer cassette)
    //   - If no virtual pole: load from normal cassette but filter only customer splitters
    // For other users: load ALL splitters (normal + customer)

    if (!isInstallTech || !virtualPole) {
      // Load splitters from normal cassette (parent pole)
      const normalCassette = freshPole.cassette || {};
      const normalSplitters = normalCassette.splitters || [];
      console.log('🔍 [InstallationTechDialog] Normal cassette splitters:', normalSplitters.length);

      // Filter for install_tech users if no virtual pole exists
      const filteredNormalSplitters = (isInstallTech && !virtualPole)
        ? normalSplitters.filter((s: any) => s.is_customer_splitter)
        : normalSplitters;

      console.log('🔍 [InstallationTechDialog] Filtered normal splitters:', filteredNormalSplitters.length);

      filteredNormalSplitters.forEach((splitter: any, idx: number) => {
        const outputs: SplitterOutput[] = (splitter.outputs || []).map((output: any, outIdx: number) => ({
          splitter_id: idx,
          splitter_type: splitter.type || '1:8',
          output_number: outIdx + 1,
          customer_name: output.customer_name,
          customer_phone: output.customer_phone,
          customer_address: output.customer_address,
          installation_notes: output.installation_notes,
          installed_by: output.installed_by,
          installed_at: output.installed_at,
          pole_id: freshPole.id, // Use parent pole ID for normal cassette
          cassette_type: 'normal',
        }));

        loadedSplitters.push({
          id: idx,
          type: splitter.type || '1:8',
          input_cable: splitter.input_cable_name,
          input_fiber: splitter.input_fiber,
          input_buffer: splitter.input_buffer,
          outputs,
        });
      });
    }

    // 2. Load splitters from customer cassette (virtual pole) if exists
    if (virtualPole) {
      const customerCassette = virtualPole.cassette || {};
      const customerSplitters = customerCassette.splitters || [];
      console.log('🔍 [InstallationTechDialog] Customer cassette splitters:', customerSplitters.length);

      customerSplitters.forEach((splitter: any, idx: number) => {
        const outputs: SplitterOutput[] = (splitter.outputs || []).map((output: any, outIdx: number) => ({
          splitter_id: idx,
          splitter_type: splitter.type || '1:8',
          output_number: outIdx + 1,
          customer_name: output.customer_name,
          customer_phone: output.customer_phone,
          customer_address: output.customer_address,
          installation_notes: output.installation_notes,
          installed_by: output.installed_by,
          installed_at: output.installed_at,
          pole_id: virtualPole.id, // Use virtual pole ID for customer cassette
          cassette_type: 'customer',
        }));

        loadedSplitters.push({
          id: idx,
          type: splitter.type || '1:8',
          input_cable: splitter.input_cable_name,
          input_fiber: splitter.input_fiber,
          input_buffer: splitter.input_buffer,
          outputs,
        });
      });
    }

    // Set actualPoleId to virtual pole if exists (for new connections)
    const actualId = virtualPole?.id || freshPole.id;
    console.log('🔍 [InstallationTechDialog] actualPoleId for new connections:', actualId);
    console.log('🔍 [InstallationTechDialog] Total splitters loaded:', loadedSplitters.length);
    setActualPoleId(actualId);

    setSplitters(loadedSplitters);
  };

  const handleOutputClick = async (output: SplitterOutput) => {
    console.log('🔍 [handleOutputClick] Clicked output:', output, 'showMoveForm:', showMoveForm, 'selectedOutput:', selectedOutput);

    // If in move mode and clicking on a free output, move the customer
    if (showMoveForm && selectedOutput && selectedOutput.customer_name && !output.customer_name) {
      console.log('🔄 [handleOutputClick] Move mode detected! Moving customer...');
      const confirmed = window.confirm(
        `Prehodiť zákazníka "${selectedOutput.customer_name}" z výstupu #${selectedOutput.output_number} na výstup #${output.output_number}?`
      );

      if (!confirmed) return;

      setLoading(true);
      try {
        // Use pole_id from selectedOutput (source) and output (destination)
        const sourcePoleId = selectedOutput.pole_id || actualPoleId;
        const destPoleId = output.pole_id || actualPoleId;
        console.log('🔍 [handleMoveCustomer] Source pole_id:', sourcePoleId, 'Dest pole_id:', destPoleId);

        // Step 1: Disconnect from old port
        const disconnectResponse = await fetch(
          `${API_URL}/api/projects/${projectId}/installation/poles/${sourcePoleId}/splitters/${selectedOutput.splitter_id}/outputs/${selectedOutput.output_number}/disconnect`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!disconnectResponse.ok) {
          throw new Error('Odpojenie zo starého portu sa nepodarilo');
        }

        // Step 2: Connect to new port
        const formDataObj = new FormData();
        formDataObj.append('customer_name', selectedOutput.customer_name);
        formDataObj.append('customer_phone', selectedOutput.customer_phone || '');
        formDataObj.append('customer_address', selectedOutput.customer_address || '');
        if (selectedOutput.installation_notes) {
          formDataObj.append('notes', selectedOutput.installation_notes);
        }

        const connectResponse = await fetch(
          `${API_URL}/api/projects/${projectId}/installation/poles/${destPoleId}/splitters/${output.splitter_id}/outputs/${output.output_number}/connect`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formDataObj,
          }
        );

        if (!connectResponse.ok) {
          throw new Error('Pripojenie na nový port sa nepodarilo');
        }

        alert('✅ Zákazník úspešne prehodený!');
        setShowMoveForm(false);
        setSelectedOutput(null);

        // Refresh poles
        await fetchPoles(projectId);

        if (onCustomerConnected) {
          onCustomerConnected();
        }
      } catch (error: any) {
        alert(`❌ Chyba: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Normal click behavior
    if (output.customer_name) {
      // Already connected - show details
      setSelectedOutput(output);
      setShowConnectForm(false);
      setShowMoveForm(false);
    } else {
      // Free - show connect form (only if not in move mode)
      if (!showMoveForm) {
        setSelectedOutput(output);
        setShowConnectForm(true);
        setFormData({
          customer_name: '',
          customer_phone: '',
          customer_address: '',
          notes: '',
        });
      }
    }
  };

  const handleConnectCustomer = async () => {
    if (!selectedOutput) return;

    // Validate based on mode
    if (connectMode === 'existing') {
      if (!selectedCustomerId) {
        alert('Vyber zákazníka zo zoznamu!');
        return;
      }
    } else {
      if (!formData.customer_name || !formData.customer_address) {
        alert('Vyplň meno aj adresu. Telefón je nepovinný.');
        return;
      }
    }

    setLoading(true);
    try {
      const formDataObj = new FormData();

      if (connectMode === 'existing') {
        // Mode 1: Link existing customer
        formDataObj.append('customer_id', selectedCustomerId!.toString());
      } else {
        // Mode 2: Manual entry
        formDataObj.append('customer_name', formData.customer_name);
        formDataObj.append('customer_phone', formData.customer_phone);
        formDataObj.append('customer_address', formData.customer_address);
      }

      if (formData.notes) {
        formDataObj.append('notes', formData.notes);
      }

      // Pri prípojke povieme serveru presne, do ktorej kazety má zapísať —
      // splitter môže stáť na inom stĺpiku, než kde technik práve je.
      if (preselect) {
        formDataObj.append('cassette_pole_id', String(selectedOutput.pole_id || actualPoleId));
      }

      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/installation/poles/${actualPoleId}/splitters/${selectedOutput.splitter_id}/outputs/${selectedOutput.output_number}/connect`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formDataObj,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Zákazníka sa nepodarilo pripojiť');
      }

      alert('✅ Zákazník úspešne pripojený!');
      setShowConnectForm(false);
      setSelectedOutput(null);
      setSelectedCustomerId(null);

      // Refresh poles from backend to get updated cassette data
      // This will trigger useEffect which calls loadSplitters()
      await fetchPoles(projectId);

      // Reload pending customers (one less now)
      loadPendingCustomers();

      if (onCustomerConnected) {
        onCustomerConnected();
      }
    } catch (error: any) {
      alert(`❌ Chyba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectCustomer = async () => {
    if (!selectedOutput) return;

    const confirmed = window.confirm(
      `Naozaj chceš odpojiť zákazníka "${selectedOutput.customer_name}" z výstupu #${selectedOutput.output_number}?`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      // Use pole_id from selectedOutput (which cassette the customer is actually connected to)
      const poleIdToUse = selectedOutput.pole_id || actualPoleId;
      console.log('🔍 [handleDisconnectCustomer] Using pole_id:', poleIdToUse, 'cassette_type:', selectedOutput.cassette_type);

      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/installation/poles/${poleIdToUse}/splitters/${selectedOutput.splitter_id}/outputs/${selectedOutput.output_number}/disconnect`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Zákazníka sa nepodarilo odpojiť');
      }

      alert('✅ Zákazník úspešne odpojený!');
      setSelectedOutput(null);

      // Refresh poles from backend to get updated cassette data
      await fetchPoles(projectId);

      if (onCustomerConnected) {
        onCustomerConnected();
      }
    } catch (error: any) {
      alert(`❌ Chyba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (!selectedOutput) return;
    setEditData({
      customer_name: selectedOutput.customer_name || '',
      customer_phone: selectedOutput.customer_phone || '',
      customer_address: selectedOutput.customer_address || '',
      notes: selectedOutput.installation_notes || '',
    });
    setShowEditForm(true);
  };

  const handleUpdateCustomer = async () => {
    if (!selectedOutput) return;
    if (!editData.customer_name || !editData.customer_address) {
      alert('Vyplň meno aj adresu. Telefón je nepovinný.');
      return;
    }
    setLoading(true);
    try {
      const poleIdToUse = selectedOutput.pole_id || actualPoleId;
      const body = new FormData();
      body.append('customer_name', editData.customer_name);
      body.append('customer_phone', editData.customer_phone);
      body.append('customer_address', editData.customer_address);
      body.append('notes', editData.notes || '');

      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/installation/poles/${poleIdToUse}/splitters/${selectedOutput.splitter_id}/outputs/${selectedOutput.output_number}/update`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Nepodarilo sa upraviť údaje.');
      }

      alert('✅ Údaje zákazníka boli upravené!');
      setShowEditForm(false);
      setSelectedOutput(null);
      await fetchPoles(projectId);
      if (onCustomerConnected) {
        onCustomerConnected();
      }
    } catch (error: any) {
      alert(`❌ Chyba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartMove = () => {
    console.log('🔄 [handleStartMove] Starting move mode, selectedOutput:', selectedOutput);
    setShowMoveForm(true);
    console.log('🔄 [handleStartMove] showMoveForm set to true');
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content installation-tech-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🔧 Montážny Checklist - {pole.name}</h2>
          {hasVirtualPole && (
            <div className="cassette-type-badge">📦 Zákaznícka kazeta</div>
          )}
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        {preselect && (
          <div style={{
            background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px',
            padding: '10px 14px', margin: '12px 16px 0', fontSize: '13px', color: '#3730a3',
          }}>
            🔌 Pripájaš zákazníka z prípojky{preselect.popis ? ` — ${preselect.popis}` : ''}.
            Splitter je v kazete stĺpika <b>{pole.name}</b>, zapíše sa to tam.
          </div>
        )}

        {/* Move Mode Banner */}
        {showMoveForm && selectedOutput && selectedOutput.customer_name && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px',
            padding: '16px',
            margin: '16px',
            boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#856404', fontSize: '18px' }}>
                🔄 Režim presunu zákazníka
              </h3>
              <button
                onClick={() => setShowMoveForm(false)}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                ✕ Zrušiť presun
              </button>
            </div>
            <div style={{ color: '#856404' }}>
              <p style={{ margin: '8px 0', fontSize: '15px' }}>
                <strong>Presúvaný zákazník:</strong> {selectedOutput.customer_name}
              </p>
              <p style={{ margin: '8px 0', fontSize: '15px' }}>
                <strong>Z portu:</strong> Splitter č.{selectedOutput.splitter_id}, Výstup #{selectedOutput.output_number}
              </p>
              <p style={{ margin: '8px 0', fontSize: '15px', fontWeight: 'bold' }}>
                👉 Klikni na <span style={{ color: '#28a745' }}>ZELENÝ voľný výstup</span> kam chceš zákazníka prehodiť
              </p>
            </div>
          </div>
        )}

        <div className="dialog-body">
          {pripojky.length > 0 && (
            <div className="pripojky-section">
              <h3>🏠 Prípojky k zákazníkom</h3>
              {pripojky.map((pr) => {
                const st = getFiberColorStyle(pr.fiber);
                const volny = !!pr.port && !pr.port.zakaznik;
                return (
                  <div key={pr.kluc} className="pripojka-item">
                    <div className="pripojka-head">
                      <span
                        className="pripojka-fiber"
                        style={{ backgroundColor: st.bg, color: st.text }}
                      >
                        vl. {pr.fiber} {getFiberColorName(pr.fiber)}
                      </span>
                      <span className="pripojka-kabel">{pr.odkial}{pr.kde}</span>
                    </div>
                    {pr.label && <div className="pripojka-popis">{pr.label}</div>}
                    {pr.port ? (
                      pr.port.zakaznik ? (
                        <div className="pripojka-obsadeny">
                          👤 <b>{pr.port.zakaznik}</b> — port {pr.port.vystup} splittra
                          {' '}{pr.port.splitterTyp} na {pr.port.poleName}
                        </div>
                      ) : (
                        <>
                          <div className="pripojka-volny">
                            ⬜ VOĽNÝ port {pr.port.vystup} splittra {pr.port.splitterTyp}
                            {' '}na {pr.port.poleName}
                          </div>
                          <button
                            className="btn-pripojit"
                            onClick={() => setPripajamZPripojky(pr)}
                          >
                            ➕ Pripojiť zákazníka na {pr.port.poleName}
                          </button>
                        </>
                      )
                    ) : (
                      <div className="pripojka-chyba">
                        ⚠️ na tomto vlákne nie je zákaznícky splitter
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {splitters.length === 0 && pripojky.length > 0 ? null : splitters.length === 0 ? (
            <div className="no-splitters">
              <p>⚠️ Tento stĺpik nemá žiadne splittre</p>
            </div>
          ) : (
            <>
              {splitters.map((splitter) => {
                const connectedCount = splitter.outputs.filter(o => o.customer_name).length;
                const freeCount = splitter.outputs.length - connectedCount;

                return (
                <div key={splitter.id} className="splitter-section">
                  <div className="splitter-header">
                    <h3>🔴 Zákaznícky splitter {splitter.type}</h3>
                    <div className="splitter-stats">
                      <span className="stat-connected">✅ {connectedCount} pripojených</span>
                      <span className="stat-separator">•</span>
                      <span className="stat-free">⚪ {freeCount} voľných</span>
                    </div>
                  </div>

                  {/* Input Info */}
                  <div className="splitter-input">
                    <h4>📥 VSTUP:</h4>
                    <div className="input-info">
                      {splitter.input_cable && (
                        <div className="input-row">
                          <span className="label">Kábel:</span>
                          <span className="value">{splitter.input_cable}</span>
                        </div>
                      )}
                      {splitter.input_buffer && (
                        <div className="input-row">
                          <span className="label">Buffer:</span>
                          <span
                            className="value fiber-badge"
                            style={{
                              backgroundColor: getBufferColorStyle(splitter.input_buffer).bg,
                              color: getBufferColorStyle(splitter.input_buffer).text,
                            }}
                          >
                            #{splitter.input_buffer} {getBufferColorName(splitter.input_buffer)}
                          </span>
                        </div>
                      )}
                      {splitter.input_fiber && (
                        <div className="input-row">
                          <span className="label">Vlákno:</span>
                          <span
                            className="value fiber-badge"
                            style={{
                              backgroundColor: getFiberColorStyle(splitter.input_fiber).bg,
                              color: getFiberColorStyle(splitter.input_fiber).text,
                            }}
                          >
                            #{splitter.input_fiber} {getFiberColorName(splitter.input_fiber)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Outputs */}
                  <div className="splitter-outputs">
                    <h4>📤 VÝSTUPY:</h4>
                    <div className="outputs-grid">
                      {splitter.outputs.map((output) => {
                        const outputColorStyle = getFiberColorStyle(output.output_number);
                        const outputColorName = getFiberColorName(output.output_number);

                        // Check if this is a valid move target (free output in move mode)
                        const isMoveTarget = showMoveForm && selectedOutput && !output.customer_name;
                        // Check if this is the source output being moved
                        const isMoveSource = showMoveForm && selectedOutput &&
                          output.splitter_id === selectedOutput.splitter_id &&
                          output.output_number === selectedOutput.output_number;

                        return (
                        <div
                          key={output.output_number}
                          className={`output-card ${output.customer_name ? 'connected' : 'free'} ${isMoveTarget ? 'move-target' : ''} ${isMoveSource ? 'move-source' : ''}`}
                          onClick={() => handleOutputClick(output)}
                          style={{
                            borderColor: isMoveTarget ? '#28a745' : (isMoveSource ? '#ffc107' : outputColorStyle.text),
                            borderWidth: (isMoveTarget || isMoveSource) ? '3px' : '2px',
                            background: output.customer_name
                              ? `linear-gradient(135deg, ${outputColorStyle.bg} 0%, ${outputColorStyle.bg} 100%)`
                              : `linear-gradient(135deg, ${outputColorStyle.bg} 0%, ${outputColorStyle.bg} 100%)`,
                            boxShadow: isMoveTarget ? '0 0 15px rgba(40, 167, 69, 0.6), 0 0 30px rgba(40, 167, 69, 0.3)' :
                                       (isMoveSource ? '0 0 15px rgba(255, 193, 7, 0.6)' : undefined),
                            cursor: isMoveTarget ? 'pointer' : (output.customer_name ? 'pointer' : 'default'),
                            animation: isMoveTarget ? 'pulse 2s infinite' : undefined,
                          }}
                        >
                          <div
                            className="output-number"
                            style={{
                              backgroundColor: outputColorStyle.text,
                              color: 'white',
                              borderColor: outputColorStyle.text,
                            }}
                          >
                            #{output.output_number}
                          </div>
                          <div
                            className="output-color-label"
                            style={{
                              color: outputColorStyle.text,
                              fontWeight: 700,
                            }}
                          >
                            {outputColorName}
                          </div>
                          {output.customer_name ? (
                            <div className="output-connected">
                              <div className="status-icon">✅</div>
                              <div className="customer-name" style={{ color: outputColorStyle.text }}>
                                {output.customer_name}
                              </div>
                              <div className="customer-phone" style={{ color: outputColorStyle.text }}>
                                {output.customer_phone}
                              </div>
                            </div>
                          ) : (
                            <div className="output-free">
                              <div className="status-icon">⚪</div>
                              <div className="status-text" style={{ color: outputColorStyle.text }}>
                                Voľný
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
              })}
            </>
          )}

          {/* Customer Details / Connect Form — počas presunu skryté, aby neprekrývalo výstupy */}
          {selectedOutput && !showConnectForm && !showEditForm && !showMoveForm && selectedOutput.customer_name && (
            <div className="customer-details-panel">
              <div className="panel-header">
                <h3>👤 Detail zákazníka - Výstup #{selectedOutput.output_number}</h3>
                <button className="btn-close-panel" onClick={() => setSelectedOutput(null)}>✕</button>
              </div>
              <div className="panel-body">
                <div className="detail-row">
                  <span className="label">Meno:</span>
                  <span className="value">{selectedOutput.customer_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Telefón:</span>
                  <span className="value">{selectedOutput.customer_phone || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Adresa:</span>
                  <span className="value">{selectedOutput.customer_address}</span>
                </div>
                {selectedOutput.installation_notes && (
                  <div className="detail-row">
                    <span className="label">Poznámky:</span>
                    <span className="value">{selectedOutput.installation_notes}</span>
                  </div>
                )}
                {selectedOutput.installed_by && (
                  <div className="detail-row">
                    <span className="label">Inštaloval:</span>
                    <span className="value">{selectedOutput.installed_by}</span>
                  </div>
                )}
                {selectedOutput.installed_at && (
                  <div className="detail-row">
                    <span className="label">Dátum:</span>
                    <span className="value">{new Date(selectedOutput.installed_at).toLocaleString('sk-SK')}</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="detail-actions">
                  <button
                    className="btn-edit"
                    onClick={handleStartEdit}
                    disabled={loading}
                  >
                    ✏️ Upraviť údaje
                  </button>
                  <button
                    className="btn-move"
                    onClick={handleStartMove}
                    disabled={loading}
                  >
                    🔄 Prehodiť na iný port
                  </button>
                  <button
                    className="btn-disconnect"
                    onClick={handleDisconnectCustomer}
                    disabled={loading}
                  >
                    🔌 Odpojiť zákazníka
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Customer Form */}
          {selectedOutput && showEditForm && (
            <div className="connect-form-panel">
              <div className="panel-header">
                <h3>✏️ Upraviť údaje - Výstup #{selectedOutput.output_number}</h3>
                <button className="btn-close-panel" onClick={() => setShowEditForm(false)}>✕</button>
              </div>
              <div className="panel-body">
                <div className="form-group">
                  <label>Meno zákazníka *</label>
                  <input
                    type="text"
                    value={editData.customer_name}
                    onChange={(e) => setEditData({ ...editData, customer_name: e.target.value })}
                    placeholder="Ján Novák"
                  />
                </div>
                <div className="form-group">
                  <label>Telefón <span className="optional-hint">(nepovinné)</span></label>
                  <input
                    type="tel"
                    value={editData.customer_phone}
                    onChange={(e) => setEditData({ ...editData, customer_phone: e.target.value })}
                    placeholder="+421 900 123 456"
                  />
                </div>
                <div className="form-group">
                  <label>Adresa *</label>
                  <input
                    type="text"
                    value={editData.customer_address}
                    onChange={(e) => setEditData({ ...editData, customer_address: e.target.value })}
                    placeholder="Hlavná 123, Bratislava"
                  />
                </div>
                <div className="form-group">
                  <label>Poznámky</label>
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    placeholder="Voliteľné poznámky k inštalácii..."
                    rows={3}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowEditForm(false)}
                    disabled={loading}
                  >
                    Zrušiť
                  </button>
                  <button
                    className="btn-connect"
                    onClick={handleUpdateCustomer}
                    disabled={loading}
                  >
                    {loading ? 'Ukladám...' : '💾 Uložiť zmeny'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Connect Customer Form */}
          {selectedOutput && showConnectForm && (
            <div className="connect-form-panel">
              <div className="panel-header">
                <h3>➕ Pripojiť zákazníka - Výstup #{selectedOutput.output_number}</h3>
                <button className="btn-close-panel" onClick={() => { setShowConnectForm(false); setSelectedOutput(null); }}>✕</button>
              </div>
              <div className="panel-body">
                {/* Mode Selector */}
                <div className="mode-selector">
                  <button
                    className={`mode-btn ${connectMode === 'existing' ? 'active' : ''}`}
                    onClick={() => setConnectMode('existing')}
                  >
                    📋 Vybrať zo zoznamu
                  </button>
                  <button
                    className={`mode-btn ${connectMode === 'manual' ? 'active' : ''}`}
                    onClick={() => setConnectMode('manual')}
                  >
                    ✍️ Zadať manuálne
                  </button>
                </div>

                {connectMode === 'existing' ? (
                  /* Existing Customer Selection */
                  <div className="form-group">
                    <label>Zákazník čakajúci na inštaláciu *</label>
                    {loadingCustomers ? (
                      <div className="loading-text">Načítavam zákazníkov...</div>
                    ) : pendingCustomers.length === 0 ? (
                      <div className="no-customers-text">
                        ⚠️ Žiadni zákazníci nečakajú na inštaláciu
                      </div>
                    ) : (
                      <select
                        value={selectedCustomerId || ''}
                        onChange={(e) => setSelectedCustomerId(e.target.value ? parseInt(e.target.value) : null)}
                        className="customer-select"
                      >
                        <option value="">-- Vyber zákazníka --</option>
                        {pendingCustomers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name} - {customer.phone} - {customer.address}
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedCustomerId && (
                      <div className="selected-customer-info">
                        {(() => {
                          const customer = pendingCustomers.find(c => c.id === selectedCustomerId);
                          return customer ? (
                            <>
                              <div><strong>Meno:</strong> {customer.name}</div>
                              <div><strong>Telefón:</strong> {customer.phone}</div>
                              <div><strong>Adresa:</strong> {customer.address}</div>
                              <div><strong>Email:</strong> {customer.email}</div>
                              <div><strong>Služba:</strong> {customer.service_type} {customer.service_speed}</div>
                            </>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Manual Entry Form */
                  <>
                    <div className="form-group">
                      <label>Meno zákazníka *</label>
                      <input
                        type="text"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        placeholder="Ján Novák"
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefón <span className="optional-hint">(nepovinné)</span></label>
                      <input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                        placeholder="+421 900 123 456"
                      />
                    </div>
                    <div className="form-group">
                      <label>Adresa *</label>
                      <input
                        type="text"
                        value={formData.customer_address}
                        onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                        placeholder="Hlavná 123, Bratislava"
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Poznámky</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Voliteľné poznámky k inštalácii..."
                    rows={3}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => { setShowConnectForm(false); setSelectedOutput(null); }}
                    disabled={loading}
                  >
                    Zrušiť
                  </button>
                  <button
                    className="btn-connect"
                    onClick={handleConnectCustomer}
                    disabled={loading}
                  >
                    {loading ? 'Pripájam...' : '✅ Pripojiť zákazníka'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Move Customer Form */}
          {selectedOutput && showMoveForm && selectedOutput.customer_name && (
            <div className="move-form-panel">
              <div className="panel-header">
                <h3>🔄 Prehodiť zákazníka na iný port</h3>
                <button className="btn-close-panel" onClick={() => setShowMoveForm(false)}>✕</button>
              </div>
              <div className="panel-body">
                <div className="move-info">
                  <p><strong>Zákazník:</strong> {selectedOutput.customer_name}</p>
                  <p><strong>Aktuálny port:</strong> Splitter č.{selectedOutput.splitter_id}, Výstup #{selectedOutput.output_number}</p>
                </div>

                <div className="move-instructions">
                  <p>ℹ️ Klikni na <strong>voľný výstup</strong> (šedý) kam chceš zákazníka prehodiť.</p>
                  <p>Zákazník bude automaticky odpojený z aktuálneho portu a pripojený na nový.</p>
                </div>

                <button
                  className="btn-cancel-move"
                  onClick={() => setShowMoveForm(false)}
                >
                  Zrušiť
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pripojenie z prípojky — splitter stojí na inom stĺpiku, otvoríme ten
          istý dialóg nad ním s predvybraným portom. */}
      {pripajamZPripojky?.port && (() => {
        const cielovy = poles.find(p => p.id === pripajamZPripojky.port.poleId);
        if (!cielovy) return null;
        return (
          <InstallationTechDialog
            pole={cielovy}
            projectId={projectId}
            preselect={{
              poleId: pripajamZPripojky.port.poleId,
              splitterIndex: pripajamZPripojky.port.splitterIndex,
              outputNumber: pripajamZPripojky.port.vystup,
              popis: `${pripajamZPripojky.odkial} vl. ${pripajamZPripojky.fiber}`
                + (pripajamZPripojky.label ? `, ${pripajamZPripojky.label}` : ''),
            }}
            onClose={() => setPripajamZPripojky(null)}
            onCustomerConnected={async () => {
              setPripajamZPripojky(null);
              await fetchPoles(projectId);
              if (onCustomerConnected) onCustomerConnected();
            }}
          />
        );
      })()}
    </div>
  );
};

